import {
  forwardRef,
  type HTMLAttributes,
  useImperativeHandle,
  useRef,
} from "react";
import { type PressEvent, usePress } from "@react-aria/interactions";

type PressableProps = Omit<HTMLAttributes<HTMLDivElement>, "onClick"> & {
  onPress?: (event: PressEvent) => void | Promise<void>;
  disabled?: boolean;
};

export const Pressable = forwardRef<HTMLDivElement, PressableProps>(
  ({ onPress, disabled, role = "button", tabIndex, style, ...props }, ref) => {
    const domRef = useRef<HTMLDivElement>(null);
    const { pressProps } = usePress({
      ref: domRef,
      isDisabled: disabled,
      onPress: (event) => {
        void onPress?.(event);
      },
    });

    useImperativeHandle(ref, () => domRef.current!);

    return (
      <div
        ref={domRef}
        role={role}
        tabIndex={disabled ? -1 : tabIndex ?? 0}
        aria-disabled={disabled || undefined}
        style={{
          touchAction: "manipulation",
          userSelect: "none",
          ...style,
        }}
        {...props}
        {...pressProps}
      />
    );
  },
);
